/* global google */

import _ from 'lodash';

import googleMapStyles from './GoogleMapStyles';
import config from '../config';

let defaultCenter = { lat: 53, lng: -1.8 };
const defaultZoom = 7;
const zoomOutLimit = 4;

let _pathToLatLngs = path => {
    var result = path.map(coords => {
        let { latitude, longitude } = coords;
        return new google.maps.LatLng(latitude, longitude);
    });

    return _.isUndefined(result.toJS) ? result : result.toJS();
};

/**
 * Decorator that logs any exceptions with console.error instead of throwing them
 * We don't want google maps errors to stop the user from progressing
 */
function errorToLog (target, name, descriptor) {
    const original = descriptor.value;
    descriptor.value = function () {
        try {
            return original.apply(this, arguments);
        } catch (e) {
            console.error("Google Maps Error", e);
            return null;
        }
    };
    return descriptor;
}

class GoogleMapsHelper {

    constructor (domNode, next) {
        this.map = null;
        this.loaded = false;
        this.domNode = domNode;
        this.next = next;
        this.objects = [];
        this.bounds = null;
        this.loadGoogleMaps();
    }

    addEventListener (event, callback) {
        if (this.map) {
            window.google.maps.event.addListener(this.map, event, callback);
        }
    }

    refresh () {
        this.bounds = new google.maps.LatLngBounds();
        _.invokeMap(this.objects, 'setMap', null);
        this.objects = [];
    }

    resize () {
        window.google.maps.event.trigger(this.map, 'resize');
    }

    /**
     * Fit bounds zooms in too close to single bounds so zoom out a little
     * by adding extra bounds
     */
    @errorToLog
    zoomOutSingleBound() {
        let bounds = this.bounds;
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
            var extendPoint1 = new google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01);
            var extendPoint2 = new google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.01);
            bounds.extend(extendPoint1);
            bounds.extend(extendPoint2);
        }
        return this.bounds;
    }

    @errorToLog
    fitBounds(bounds = this.bounds) {
        if (!bounds.isEmpty()) {
            this.map.fitBounds(this.zoomOutSingleBound());
        } else {
            this.map.setCenter(defaultCenter);
            this.map.setZoom(defaultZoom);
        }
    }

    loadGoogleMaps () {
        if (!window.google) {

            const existingCallback = window.callback;
            window.callback = this.callback;

            //Avoid loading the script if there is already a pending callback
            if (!existingCallback) {
                let script = document.createElement('script');
                script.setAttribute('src', config.get('googleMapsSrc') + config.get('googleMapsApiKey'));
                document.head.appendChild(script);
            }
        } else {
            // Due to async nature of loading/rendering
            setTimeout(this.callback.bind(this), 0);
        }
    }

    callback = () => {
        window.callback = void 0;
        delete window.callback;
        this.next(this.createMap());
    };

    createMap () {
        this.map = new google.maps.Map(this.domNode, {
            zoom: defaultZoom,
            minZoom: zoomOutLimit,
            draggable: true,
            zoomControl: true,
            scrollwheel: true,
            disableDefaultUI: false,
            disableDoubleClickZoom: false,
            center: defaultCenter,
            styles: googleMapStyles,
            mapTypeControl: false,
            streetViewControl: false
        });

        this.loaded = true;
        return this.map;
    }

    dashedLine (path, color = config.get('googleMapPrimaryColour')) {
        let lineSymbol = {
            path: 'M 0,-1 0,1',
            strokeColor: color,
            strokeOpacity: 1,
            scale: 4
        };

        let dashed = new google.maps.Polyline({
            path: _pathToLatLngs(path),
            strokeOpacity: 0,
            icons: [ { icon: lineSymbol, offset: '0', repeat: '25px' } ],
            map: this.map
        });

        path.forEach(coords => {
            let marker = document.createElement('div');
            marker.style.background = color;
            marker.className = 'map__calling-point';
            this.newLabel(marker, coords);
        });

        this.objects.push(dashed);
    }

    solidLine (path, color = config.get('googleMapPrimaryColour')) {
        path = path.map((coords) => {
            if (!coords.latitude || !coords.longitude) {
                return null;
            }

            const { latitude, longitude } = coords;
            const latLng = new google.maps.LatLng(latitude, longitude);
            this.bounds.extend(latLng);
            return latLng;
        }).toJS();

        const solid = new google.maps.Polyline({
            map: this.map,
            path,
            strokeColor: color,
            strokeOpacity: 1,
            strokeWeight: 4,
            clickable: false,
            editable: false,
            visible: true
        });

        this.objects.push(solid);
    }

    points (path, color = config.get('googleMapPrimaryColour')) {
        path.forEach((coords) => {
            let marker = document.createElement('div');
            marker.style.background = color;
            marker.className = 'map__calling-point';
            this.newLabel(marker, coords);
        });
    }

    newLabel (element, coords) {
        if (!coords.latitude || !coords.longitude) {
            return null;
        }

        let latLng = new google.maps.LatLng(coords.latitude, coords.longitude);
        let map = this.map;
        let Overlay = function () {
            this.setMap(map);
        };
        Overlay.prototype = new google.maps.OverlayView();
        Overlay.prototype.onAdd = function () {
            this.getPanes().floatPane.appendChild(element);
        };
        Overlay.prototype.draw = function () {
            var projection = this.getProjection();
            var pos = projection.fromLatLngToDivPixel(latLng);
            element.style.position = 'absolute';
            element.style.left = pos.x + 'px';
            element.style.top = pos.y + 'px';
        };
        Overlay.prototype.onRemove = function () {
            if (element.parentNode) {
                element.parentNode.removeChild(element);
            }
            element = null;
        };

        let label = new Overlay();

        this.objects.push(label);
        this.bounds.extend(latLng);

        return label;
    }

    getDistance (x, y) {
        return google.maps.geometry.spherical.computeDistanceBetween(x, y);
    }

    getRoutePairs (route) {
        return route.map((x, i) => [ x, route[i + 1] ])
            .splice(0, route.length - 2); // Remove last one
    }

    getRouteDistance (route) {
        return google.maps.geometry.spherical.computeLength(route);
    }

    getRouteMiddle (route) {
        let latLngs = _pathToLatLngs(route),
            roughMiddle = this.getRouteDistance(latLngs) / 2,
            pairs = this.getRoutePairs(latLngs),
            distances = pairs.map((xCoord) => this.getDistance(...xCoord)),
            runningTotal = 0;

        for (let i = 0; i < distances.length; i++) {
            let curDistance = runningTotal + distances[i];

            if (curDistance >= roughMiddle) {
                let [ x, y ] = pairs[i],
                    p = (roughMiddle - runningTotal) / (curDistance - runningTotal);

                return google.maps.geometry.spherical.interpolate(x, y, p);
            }

            runningTotal = curDistance;
        }

        const [ posX, posY ] = _.takeRight(latLngs, 2);
        if (!posX || !posY) { return _.head(latLngs); }
        return google.maps.geometry.spherical.interpolate(posX, posY, 0.5);
    }

    getZoomLevel () {
        return this.map.getZoom();
    }

    setZoomLevel (level) {
        this.map.setZoom(level);
    }

    setCenter (pos) {
        this.map.setCenter(pos);
    }

    addMarker ({ animation, ...options }) {
        if (animation) {
            animation = google.maps.Animation[animation];
        }
        return new google.maps.Marker(
            Object.assign({}, options, { map: this.map, animation })
        );
    }
}

export default GoogleMapsHelper;



// WEBPACK FOOTER //
// ./src/helpers/GoogleMaps.js